import { API_BASE } from "./apiBase";
// --- Profit / ROI math ---
// Single source of truth for every page that shows profit or ROI (tables, mobile
// cards, sorting, filtering, CSV export). Keeping the math here is what stops a
// permanent cost (sales tax, prep) from being applied on one screen but not another.

// Permanent costs the user sets once in the settings modal.
export const getSalesTax = () => parseFloat(localStorage.getItem("salesTax")) || 0;
export const getPrepCost = () => parseFloat(localStorage.getItem("costPrep")) || 0;

// Amazon fees. Endpoints disagree on the field names: the retail/wholesale feeds
// send absolute values (selling_on_az_fees / fba_cost), the live feeds send a
// referral percentage (ref_fee_perc / fba_fee).
const refFee = (item) =>
	item.selling_on_az_fees ?? item.ref_fee_perc * item.az_price;
const fbaFee = (item, filters) =>
	filters.isFBM ? 0 : item.fba_cost ?? item.fba_fee;

// What the unit actually costs to land: discounted supplier price + prep + sales tax.
export const productBuyCost = (item, filters = {}, prep = getPrepCost()) =>
	item.price * (1 - (filters.priceCoupon || 0) / 100) +
	(parseFloat(prep) || 0) +
	item.price * (getSalesTax() / 100);

export const productProfit = (item, filters = {}, prep = getPrepCost()) =>
	item.az_price - refFee(item) - fbaFee(item, filters) - productBuyCost(item, filters, prep);

// Fraction, not percent — same convention as the backend `roi` field it replaces.
export const productRoi = (item, filters = {}, prep = getPrepCost()) =>
	productProfit(item, filters, prep) / productBuyCost(item, filters, prep);

// --- Cookie helpers (used by the logged-in /home roadmap) ---
export const setCookie = (name, value, days = 365) => {
	const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
	document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
};

export const getCookie = (name) => {
	const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
	return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : "";
};

const ROADMAP_COOKIE = "nepeto_roadmap";
const ROADMAP_STORAGE_KEY = "nepeto_roadmap";

// Persists the completed step ids as a JSON array in localStorage.
const writeCompletedSteps = (steps) => {
	if (typeof window === "undefined" || !window.localStorage) return;
	try {
		window.localStorage.setItem(ROADMAP_STORAGE_KEY, JSON.stringify(steps));
	} catch (e) {
		// localStorage may be unavailable (private mode, quota) — fail silently.
	}
};

// Returns array of completed step ids (strings), e.g. ["1","3"]
export const getCompletedSteps = () => {
	if (typeof window === "undefined" || !window.localStorage) return [];

	let raw = "";
	try {
		raw = window.localStorage.getItem(ROADMAP_STORAGE_KEY) || "";
	} catch (e) {
		raw = "";
	}

	if (raw) {
		try {
			const parsed = JSON.parse(raw);
			if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
		} catch (e) {
			// Corrupt value — fall through to treat as empty / re-migrate.
		}
	}

	// One-time back-compat migration from the old cookie.
	const legacy = getCookie(ROADMAP_COOKIE);
	if (legacy) {
		const migrated = legacy.split(",").filter(Boolean);
		writeCompletedSteps(migrated);
		return migrated;
	}

	return [];
};

// Marks a step complete (idempotent) and persists to localStorage. Returns the new list.
export const markStepComplete = (id) => {
	const current = getCompletedSteps();
	if (!current.includes(String(id))) {
		current.push(String(id));
		writeCompletedSteps(current);
	}
	return current;
};

export const checkGatedBatch = async (sellerId, asins, refreshToken, onBatch) => {
	const batchSize = 15;

	const batches = [];
	for (let i = 0; i < asins.length; i += batchSize) {
		batches.push(asins.slice(i, i + batchSize));
	}

	const aggregate = {};
	await Promise.all(batches.map(async (batch) => {
		try {
			const res = await fetch(`${API_BASE}/mobile_app_check_restrictions/batch`, {
				mode: "cors",
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					sellerId,
					asins: batch,
					refreshToken,
				}),
			});
			if (!res.ok) {
				throw new Error(`Request failed with status ${res.status}`);
			}
			const data = await res.json();
			const complete = {};
			batch.forEach((a) => { complete[a] = data[a] ?? { error: true }; });
			Object.assign(aggregate, complete);
			if (onBatch) onBatch(complete);
		} catch (error) {
			console.error("checkGatedBatch batch failed:", error);
			const errored = {};
			batch.forEach((a) => { errored[a] = { error: true }; });
			Object.assign(aggregate, errored);
			if (onBatch) onBatch(errored);
		}
	}));

	return aggregate;
};

// Rows carry the full Amazon URL in `asin`; every eligibility lookup keys off the bare
// ASIN at the end of it.
export const productAsin = (product) => {
	const parts = product.asin.split("/");
	return parts[parts.length - 1];
};

// Same status logic as the desktop table (ProductsTable) and the Excel export:
// green = ungated, orange = approval required, red = gated, white = not checked
// (no seller connected, check still in flight, or the check errored).
export const getGatedColor = (gatedStatus, product) => {
	const asin = productAsin(product);
	if (gatedStatus && gatedStatus.hasOwnProperty(asin) && gatedStatus[asin].restrictions) {
		if (gatedStatus[asin].restrictions.length === 0) {
			return "green";
		} else if (gatedStatus[asin].restrictions[0].reasons[0].reasonCode === "APPROVAL_REQUIRED") {
			return "orange";
		} else {
			return "red";
		}
	}
	return "white";
};

export const passesGatedFilters = (filters, gatedStatus, product) => {
	const color = getGatedColor(gatedStatus, product);
	return (
		(!filters.filterOutGated || color !== "red") &&
		(!filters.showOnlyUngated || color === "green")
	);
};

export const gatedFiltersActive = (filters) =>
	Boolean(filters && (filters.filterOutGated || filters.showOnlyUngated));

// The rows a gated filter actually leaves visible. This has to run BEFORE the page slice,
// never per rendered row: filtering a fixed window of ten can only shrink it, which is what
// produced short and empty pages while the ungated rows that should have filled them sat on
// a later page. Deliberately NOT a reorder -- moving ungated rows to the front would override
// whichever column the user sorted by.
export const gatedVisibleProducts = (products, filters, gatedStatus) => {
	if (!gatedFiltersActive(filters)) {
		return products;
	}
	return products.filter((product) => passesGatedFilters(filters, gatedStatus ?? {}, product));
};

// The next ASINs worth asking Amazon about, in the order the rows are already sorted.
// Eligibility is per ASIN, so rows sharing one (the same product at several suppliers) cost a
// single check, and `limit` counts distinct ASINs rather than rows. Skipping `inFlight` matters
// because results merge one 15-ASIN batch at a time, re-running the caller mid-sweep.
export const nextUncheckedAsins = (products, gatedStatus, inFlight, limit) => {
	const asins = [];
	const seen = new Set();
	for (const product of products) {
		if (asins.length >= limit) break;
		const asin = productAsin(product);
		if (seen.has(asin)) continue;
		seen.add(asin);
		if (gatedStatus && Object.prototype.hasOwnProperty.call(gatedStatus, asin)) continue;
		if (inFlight && inFlight.has(asin)) continue;
		asins.push(asin);
	}
	return asins;
};

// How many distinct ASINs the rows cover, and how many of those already have an answer --
// the numbers the "checked N of M" checkpoint reports.
export const gatedCheckProgress = (products, gatedStatus) => {
	const asins = new Set(products.map(productAsin));
	let checked = 0;
	asins.forEach((asin) => {
		if (gatedStatus && Object.prototype.hasOwnProperty.call(gatedStatus, asin)) checked += 1;
	});
	return { total: asins.size, checked };
};

// Amazon does not rank every product -- media, apparel variation children and long-tail items
// often carry no Best Sellers Rank at all. The pipeline used to write a 10,000,000 sentinel so the
// column could stay NOT NULL, and that number reached the table as a real, catastrophic rank. It
// is NULL now, and an absent rank reads as absent.
export const bsrLabel = (rank) => (rank > 0 ? `#${rank}` : "N/A");

// The "Top x%" figure divides by a category count, so it only means anything with a real rank.
export const hasBsr = (rank) => rank > 0;

// One shuffle seed per page load. The lead tables default to "Last Update" descending, so the
// same rows sat at the top on every visit and the section looked stale even while the pipeline
// rebuilt it hourly. The backend orders by a hash of (row key, seed) when this is present: a new
// order on each refresh, and a STABLE one across the pages of that refresh -- which ORDER BY
// random() cannot give, since it re-rolls per statement and would show a lead twice or not at all.
export const SHUFFLE_SEED = Math.random().toString(36).slice(2);

// Merge the seed into a filters object on its way to the API without storing it in filter state,
// where it would end up in saved filters and in the URL.
export const withSeed = (filters) => ({ ...(filters || {}), seed: SHUFFLE_SEED });

// Seller ids that ARE Amazon. Kept in one place because they were duplicated across the backend
// filter and five call sites here, so adding one meant seven edits and missing one was silent.
// Mirrors AMAZON_SELLER_IDS in nepeto-backend/nepeto_backend/consts.py -- keep the two in step.
// A2L77EE7U53NWQ is Amazon Warehouse. Note ATVPDKIKX0DER is ALSO the US marketplace id in SP-API
// calls; that is a different thing and is not this list.
export const AMAZON_SELLER_IDS = ["ATVPDKIKX0DER", "A2R2RITDJNW1Q6", "A2L77EE7U53NWQ"];

export const isAmazonSeller = (sellerId) => AMAZON_SELLER_IDS.includes(sellerId);

// Render a seller id for humans: Amazon's own ids become "Amazon", everything else is the id.
export const sellerLabel = (sellerId) =>
  isAmazonSeller(sellerId) ? "Amazon" : (sellerId || "Unknown seller");
